import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from '@hqo/ui-atoms';
import { ChevronDown, Clock } from 'lucide-react';

import { AddOn } from 'store/resource/types';
import { displayUnitPriceCents } from 'store/resource/price-tiers';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { formatCurrency } from 'utils/formatCurrency';
import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useLocale } from 'hooks/use-locale.hook';
import { addOnMessage } from 'pages/booking-add-ons/add-on-message';
import { addOnSubtitle } from 'pages/booking-add-ons/add-on-subtitle';
import { AddOnProductIcon } from 'components/add-on-product-icon';
import { QuantityStepper } from 'components/quantity-stepper';

export interface AddOnCardProps {
  addOn: AddOn;
  quantity: number;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  /* Drop the whole line. The fixed-quantity button's action — a decrement would leave a remainder. */
  onRemove?: (addOn: AddOn) => void;
  isExpanded: boolean;
  onToggle: (addOnId: string) => void;
  /* This add-on's party is not the one the tenant committed to. */
  isPartyLocked?: boolean;
  /* RES-1398. Kept past its notice window because the booking already carries it. */
  isKept?: boolean;
  /* The product's range admits exactly one quantity, so the stepper becomes an Add/Remove button. */
  isFixedQuantity?: boolean;
  /* At the product's ceiling. Styling only — the refusal lives in the hook's `onIncrease`. */
  isAtMax?: boolean;
  /* The quantity-range read is in flight, so the bands this product prices by are not known yet. */
  isAwaitingRanges?: boolean;
  /* The smallest quantity the tenant could order, for pricing an unselected card. */
  minQuantity?: number;
  /* The booking's start, which dates the "Not available for Sep 24" line. */
  bookingStart?: string;
}

/**
 * VIS-2193 — one add-on as a card, built around its product image. Used by both tenant add-on
 * surfaces (the post-booking step and the my-bookings dialog), behind `show-product-images`.
 *
 * Layout follows the catalog-admin product preview: the thumbnail (the catalog image, or the `Package`
 * box when there is none or it fails to load), the name over the price, a chevron when there is more
 * to show; then the lead-time notice; then the quantity control with the line total beside it.
 *
 * Which control to draw and when adding is held are decided by `useBookingAddOns` and handed in, so
 * the rules are shared with the older rows rather than copied.
 */
export const AddOnCard = ({
  addOn,
  quantity,
  onIncrease,
  onDecrease,
  onRemove,
  isExpanded,
  onToggle,
  isPartyLocked = false,
  isKept = false,
  isFixedQuantity = false,
  isAtMax = false,
  isAwaitingRanges = false,
  minQuantity = 1,
  bookingStart,
}: AddOnCardProps): JSX.Element => {
  const intl = useIntl();
  const buildingCurrency = useCurrencyType();
  const locale = useLocale();
  /* The building's zone, so the day named in the notice cannot disagree with the booking's day. */
  const timeZone = useSelector(selectBuildingTimeZone);

  /* Per-item currency: one step can list several vendors' products. The building's is the fallback. */
  const currency = addOn.currency ?? buildingCurrency;
  /* Priced at the quantity shown, so a tiered product re-prices as the stepper crosses a band. */
  const unitPriceCents = displayUnitPriceCents(addOn, quantity, minQuantity);
  const format = (cents: number): string => formatCurrency(cents, currency, locale, true, false);

  /* `pricing_type`, not the number: a quote-required product arrives as `price: 0`, like a free one. */
  const isQuote = addOn.pricing_type === 'quote_required';
  const isFree = !isQuote && (addOn.pricing_type === 'free' || unitPriceCents === 0);
  const subtitle = addOnSubtitle(addOn, { intl, price: format(unitPriceCents), quantity, minQuantity });
  /* Nothing to total before anything is chosen, nor for a free or quoted product. */
  const lineTotal = quantity > 0 && !isQuote && !isFree ? format(unitPriceCents * quantity) : null;

  const hasDetails = Boolean(addOn.description || addOn.image_url);
  /* Read off the payload, not recomputed: helix decides it and the submit path agrees. */
  const isUnavailable = Boolean(addOn.unavailable_reason);
  const notice = addOnMessage(addOn, { intl, locale, bookingStart, timeZone, isKept });
  /*
   * Nothing can be ADDED from a locked party, while the range read is in flight, or past the notice
   * window. Removing stays available throughout, so a line already added can never be stranded.
   */
  const canAdd = !isPartyLocked && !isAwaitingRanges && !isUnavailable;
  /*
   * No control at all when nothing can be done: past the window with nothing chosen (the notice says
   * why), or a locked party with nothing chosen (its heading says why). A greyed stepper invites a
   * press that does nothing.
   */
  const hasControl = quantity > 0 || (!isUnavailable && !isPartyLocked);

  const handleToggle = useCallback(() => onToggle(addOn.id), [onToggle, addOn.id]);
  const handleIncrease = useCallback(() => (canAdd ? onIncrease(addOn) : undefined), [canAdd, onIncrease, addOn]);
  const handleDecrease = useCallback(() => onDecrease(addOn), [onDecrease, addOn]);
  /* Falls back to the decrement only for callers that predate `onRemove`. */
  const handleRemove = useCallback(
    () => (onRemove ? onRemove(addOn) : onDecrease(addOn)),
    [onRemove, onDecrease, addOn],
  );

  const titleId = `add-on-card-title-${addOn.id}`;
  const detailsId = `add-on-card-details-${addOn.id}`;
  const noticeId = `add-on-card-notice-${addOn.id}`;
  /* The notice explains an unavailable card. A locked party says so once, on its heading. */
  const describedBy = isUnavailable ? noticeId : undefined;

  const headerContent = (
    <>
      <AddOnProductIcon imageUrl={addOn.image_url} imageAltText={addOn.image_alt_text} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span id={titleId} className="text-sm font-medium break-words text-foreground">
          {addOn.name}
        </span>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </span>
      {hasDetails && (
        <ChevronDown
          aria-hidden="true"
          className={`mt-1 size-4 shrink-0 text-muted-foreground transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      )}
    </>
  );

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border bg-background p-3 text-foreground"
      data-testid={`add-on-card-${addOn.id}`}
    >
      {/*
        A disclosure only when there is something to disclose: a button that toggles nothing is a dead
        tab stop. `data-slot="button"` opts into the app's scoped button reset (app.css), which clears
        the UA border and background but not its padding or centred text, hence `p-0 text-left`.
      */}
      {hasDetails ? (
        <button
          type="button"
          data-slot="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={detailsId}
          aria-describedby={describedBy}
          className="flex w-full cursor-pointer items-start gap-3 rounded-lg p-0 text-left focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          data-testid={`add-on-card-toggle-${addOn.id}`}
        >
          {headerContent}
        </button>
      ) : (
        <div className="flex items-start gap-3" data-testid={`add-on-card-header-${addOn.id}`}>
          {headerContent}
        </div>
      )}

      {isExpanded && hasDetails && (
        <div id={detailsId} className="flex flex-col gap-2" data-testid={`add-on-card-details-${addOn.id}`}>
          {/* The same image as the thumbnail, which already carries the catalog alt text. */}
          {addOn.image_url && (
            <img src={addOn.image_url} alt="" className="block max-h-40 w-full rounded-lg object-cover" />
          )}
          {addOn.description && <p className="m-0 text-sm text-muted-foreground">{addOn.description}</p>}
        </div>
      )}

      {notice && (
        <span
          id={noticeId}
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
          data-testid={`add-on-card-notice-${addOn.id}`}
        >
          <Clock className="size-3.5 shrink-0" aria-hidden="true" />
          {notice}
        </span>
      )}

      {hasControl && (
        <div className="flex items-center gap-2">
          {isFixedQuantity ? (
            /*
             * The range admits exactly one quantity, so there is nothing to step. The label names the
             * action; removal uses `handleRemove`, never a decrement, which could leave a remainder.
             */
            <Button
              type="button"
              id={`add-on-card-pill-${addOn.id}`}
              variant={quantity > 0 ? 'outline' : 'default'}
              size="sm"
              onClick={quantity > 0 ? handleRemove : handleIncrease}
              disabled={!canAdd && quantity === 0}
              aria-labelledby={`add-on-card-pill-${addOn.id} ${titleId}`}
              data-testid={`add-on-card-pill-${addOn.id}`}
            >
              {intl.formatMessage({ id: quantity > 0 ? 'add_on_remove' : 'add_on_add' })}
            </Button>
          ) : (
            <QuantityStepper
              value={quantity}
              onIncrease={handleIncrease}
              onDecrease={handleDecrease}
              canDecrease={quantity > 0}
              canIncrease={canAdd && !isAtMax}
              decreaseLabel={intl.formatMessage({ id: 'add_on_remove' })}
              increaseLabel={intl.formatMessage({ id: 'add_on_add' })}
              itemLabelId={titleId}
              unitLabel={intl.formatMessage({ id: 'add_on_quantity_units' }, { count: quantity })}
              testIdPrefix={`add-on-card-stepper-${addOn.id}`}
            />
          )}
          {lineTotal && (
            <span
              className="ml-auto text-sm font-medium text-foreground tabular-nums"
              data-testid={`add-on-card-line-total-${addOn.id}`}
            >
              {lineTotal}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
